import React from 'react';
import { Box } from '@mui/material';

import { colors } from '../../theme/tokens';

/**
 * EnvPill — the ONE canonical environment identity used across the app
 * (top-bar switcher, version board, authoring card, editing-env map, banners).
 *
 * Replaces the pale 10px dots that were reimplemented inline in several places:
 * a tinted rounded pill + a SATURATED dot + an uppercase label, so dev01 / qa /
 * uat / integ / prod each read distinctly and prod reads as a warning by colour.
 *
 *   dev01            amber  (the editable home colour)
 *   qa / uat / integ blue
 *   prod             red    (only ever shown read-only)
 *
 * Sizes: sm (chips/tables), md (default), lg (hero/cards). `dotOnly` renders
 * just the saturated dot (no pill chrome) for ultra-dense rows.
 */
export type EnvPillSize = 'sm' | 'md' | 'lg';

interface EnvStyle {
  bg: string;
  border: string;
  text: string;
  dot: string;
}

/** Saturated dot + tinted pill palette per env family. */
export function envPillStyle(env: string): EnvStyle {
  if (env === 'dev01') {
    return { bg: colors.brandLight, border: colors.warningBorder, text: colors.warningText, dot: colors.envDev };
  }
  if (env === 'prod') {
    return { bg: colors.errorBgStrong, border: colors.errorBorder, text: colors.errorText, dot: colors.envProd };
  }
  // qa / uat / integ / anything else → blue
  return { bg: colors.infoBg, border: colors.infoBorder, text: colors.infoText, dot: colors.infoIcon };
}

const SIZES: Record<EnvPillSize, { fontSize: number; padY: number; padX: number; dot: number; gap: number }> = {
  sm: { fontSize: 10, padY: 0.25, padX: 0.75, dot: 7, gap: 0.6 },
  md: { fontSize: 11, padY: 0.4, padX: 1, dot: 8, gap: 0.75 },
  lg: { fontSize: 13, padY: 0.6, padX: 1.25, dot: 9, gap: 0.9 },
};

const EnvPill: React.FC<{
  env: string;
  size?: EnvPillSize;
  showLabel?: boolean;
  /** Render just the saturated dot, no pill chrome (dense rows). */
  dotOnly?: boolean;
}> = ({ env, size = 'md', showLabel = true, dotOnly = false }) => {
  const s = envPillStyle(env);
  const dim = SIZES[size];

  if (dotOnly) {
    return (
      <Box
        data-testid={`env-badge-${env}`}
        title={env}
        sx={{ width: dim.dot + 2, height: dim.dot + 2, borderRadius: '50%', backgroundColor: s.dot, flexShrink: 0 }}
      />
    );
  }

  return (
    <Box
      data-testid={`env-badge-${env}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: dim.gap,
        px: dim.padX,
        py: dim.padY,
        borderRadius: '999px',
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
        color: s.text,
        fontFamily: 'Manrope, sans-serif',
        fontSize: dim.fontSize,
        fontWeight: 800,
        letterSpacing: '0.4px',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
      }}
    >
      <Box sx={{ width: dim.dot, height: dim.dot, borderRadius: '50%', backgroundColor: s.dot, flexShrink: 0 }} />
      {showLabel && <span>{env}</span>}
    </Box>
  );
};

export default EnvPill;
